import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import BriefBlogCard from './BriefBlogCard';
import { TrendingBlogPost } from '../../../../pages/blog';
import Link from 'next/link';
import { formatCreatedAt } from '../../../../utils/getDateTime';
import { useMediaQuery } from '../../../../hooks/mediaQuery';

interface TrendingBlogsProps {
  blogs: TrendingBlogPost[];
}

export default function TrendingBlogs({ blogs }: TrendingBlogsProps) {
  const [cardsToShow, setCardsToShow] = useState(3);
  const isMobileOrTabletScreen = useMediaQuery(1280);

  useEffect(() => {
    // Function to update the number of cards based on screen width
    const handleResize = () => {
      const screenWidth = window.innerWidth;

      if (screenWidth <= 639) {
        setCardsToShow(3); // Show 3 cards for screens between 250 and 639
      } else if (screenWidth >= 640 && screenWidth <= 1023) {
        setCardsToShow(6); // Show 6 cards for screens between 640 and 1023
      } else {
        setCardsToShow(4); // Show all cards for larger screens
      }
    };

    // Call the function on initial load
    handleResize();

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return (
    <div
      className={`lg:border-[#D6D6D6] lg:border lg:p-5 w-full lg:w-[50%] xl:w-[78%] 2xl:w-[73%] 3xl:w-[65%] flex flex-col gap-5`}
    >
      <h2 className="text-black2 text-xl sm:text-[22px] md:text-[26px] 2xl:text-3xl 3xl:text-4xl font-semibold tracking-[0.4px]">
        Trending
      </h2>
      <div className="flex justify-between w-full">
        <div className="xxsm:hidden xl:flex xl:w-[53%] 2xl:w-[55%] 3xl:w-[57%] flex flex-col gap-3">
          <Link
            className="h-[247px] 2xl:h-[247px] 3xl:h-[270px] 4xl:h-[300px] !overflow-hidden"
            href={`/blog/${blogs?.[0]?.slug}`}
          >
            <Image
              src={blogs?.[0]?.image?.url}
              width={466}
              height={247}
              alt={blogs?.[0]?.image?.alternativeText ?? 'trending-image'}
              className="w-full h-full hover:scale-105 ease-in-out duration-200 cursor-pointer"
            />
          </Link>
          <div className="flex flex-col gap-3 w-full">
            <p className="text-gray75 text-xs font-medium">
              {formatCreatedAt(blogs?.[0]?.created_at)}
            </p>

            <div className="text-black2 text-[17px] font-semibold">
              {blogs?.[0]?.page_title?.includes('-') &&
              blogs?.[0]?.page_title?.split('-')?.pop()?.trim() ===
                'See Sight Tours'
                ? blogs?.[0]?.page_title
                    ?.split('-')
                    .slice(0, -1)
                    .join('-')
                    .trim()
                : blogs?.[0]?.page_title}
            </div>
            <div
              id="description"
              className="h-[63px] overflow-hidden text-[#6e6e6e] text-sm font-normal leading-[21px]"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical'
              }}
            >
              <p>{blogs?.[0]?.snippet}</p>
            </div>
            <Link
              className="text-black2 text-base border-b border-b-black2 w-[80px] h-[40px] items-center flex"
              href={`/blog/${blogs?.[0]?.slug}`}
              target="_blank"
            >
              View Blog
            </Link>
          </div>
        </div>
        <div className="md:w-full xl:w-[44%] 2xl:w-[42%] 3xl:w-[40%]">
          <div
            className={`grid gap-[22px] ${
              cardsToShow <= 3 || cardsToShow === 4
                ? 'grid-cols-1'
                : 'grid-cols-2'
            }`}
          >
            {blogs
              ?.slice(isMobileOrTabletScreen ? 0 : 1, cardsToShow)
              ?.map((blog, index) => (
                <BriefBlogCard key={index} blog={blog} />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
